
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ShippingOption from './ShippingOption';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';

// Shipping options type
export interface ShippingOptionType {
  id: string;
  name: string;
  price: number;
  days: string;
}

const shippingSchema = z.object({
  address: z.string().min(5, { message: "Address must be at least 5 characters" }),
  city: z.string().min(2, { message: "City must be at least 2 characters" }),
  district: z.string().min(2, { message: "District must be at least 2 characters" })
});

export type ShippingFormValues = z.infer<typeof shippingSchema>;

interface ShippingInformationProps {
  shippingOptions: ShippingOptionType[];
  selectedShipping: string;
  setShipping: (id: string) => void;
  currency: string;
  onSubmit?: (values: ShippingFormValues) => void;
  defaultValues?: Partial<ShippingFormValues>;
}

const ShippingInformation: React.FC<ShippingInformationProps> = ({
  shippingOptions,
  selectedShipping,
  setShipping,
  currency,
  onSubmit = () => {},
  defaultValues = {
    address: '',
    city: '',
    district: ''
  }
}) => {
  const form = useForm<ShippingFormValues>({
    resolver: zodResolver(shippingSchema),
    defaultValues
  });

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-semibold mb-4">Shipping Information</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="address">Address</Label>
                <FormControl>
                  <Input id="address" placeholder="Street Address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="city">City</Label>
                  <FormControl>
                    <Input id="city" placeholder="City" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="district"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="district">District</Label>
                  <FormControl>
                    <Input id="district" placeholder="District" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div>
            <Label className="mb-2 block">Shipping Method</Label>
            <div className="space-y-3">
              {shippingOptions.map((option) => (
                <ShippingOption
                  key={option.id}
                  id={option.id}
                  name={option.name}
                  price={option.price}
                  days={option.days}
                  selected={selectedShipping === option.id}
                  currency={currency}
                  onSelect={setShipping}
                />
              ))}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ShippingInformation;
